import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { EventDetails, Favourite } from '../types';

// The signed-in user's favourited event ids, fetched once per page, with a
// toggle that adds or removes an event (favourites make up the event planner).
export const useFavourites = () => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [ids, setIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!session) {
      setIds(new Set());
      return;
    }
    let cancelled = false;
    api
      .get<Favourite[] | null>('/getfavourites')
      .then((r) => {
        if (!cancelled) setIds(new Set((r.data ?? []).map((f) => f.EventId)));
      })
      .catch((err) => console.error('Error fetching favourites:', err));
    return () => {
      cancelled = true;
    };
  }, [session]);

  const toggle = useCallback(
    async (event: EventDetails) => {
      if (!session) {
        navigate('/login', { state: { from: location.pathname + location.search } });
        return;
      }
      const adding = !ids.has(event.Id);
      const update = (add: boolean) =>
        setIds((prev) => {
          const next = new Set(prev);
          if (add) next.add(event.Id);
          else next.delete(event.Id);
          return next;
        });

      update(adding);
      try {
        const payload = { EventId: event.Id, EventName: event.Name };
        if (adding) await api.post('/addfavourite', payload);
        else await api.delete('/deletefavourite', { data: payload });
      } catch (err) {
        console.error('Error updating favourite:', err);
        update(!adding); // undo
      }
    },
    [session, ids, navigate, location]
  );

  return { favouriteIds: ids, toggleFavourite: toggle };
};
